import { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CalendarDays, Clock, Video, Instagram, Youtube, X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

/* ---- UTM capture: keep ad-campaign params across the visit and send them with the signup ---- */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const UTM_STORE_KEY = "mc_utms";

const captureUtms = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utms = JSON.parse(sessionStorage.getItem(UTM_STORE_KEY) || "{}");
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) utms[key] = value.slice(0, 200);
    });
    sessionStorage.setItem(UTM_STORE_KEY, JSON.stringify(utms));
  } catch { /* storage unavailable */ }
};

const getStoredUtms = () => {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_STORE_KEY) || "{}");
  } catch {
    return {};
  }
};

/* ---- Assets ---- */
const HERO_QUER = "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/12j1k1hx_Eigentlich_Hero-LP-quer.webp";
const HERO_HOCH = "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/43x53fcv_Eigentlich_Hero-LP-hoch.png";
const ABOUT_PORTRAIT = "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/ha8r2yfd_IMG_7464.jpg";
const INTRO_VIDEO_MP4 = "/eigentlich-intro.mp4";
const INTRO_VIDEO_WEBM = "/eigentlich-intro.webm";

const DATENSCHUTZ_URL = "https://luisabergholz.com/datenschutzerklaerung";

/* ---- Testimonials (identical to SOYT) ---- */
const testimonials = [
  { quote: "Dank Dir und dem tollen Workshop habe ich tatsächlich meinen Traumjob gefunden - und mich sogar selbstständig gemacht. Etwas, von dem ich nie gedacht hätte, dass ich es jemals sagen würde. Erst durch Dich habe ich begonnen, meine Glaubenssätze zu hinterfragen - und bin jetzt so happy, wie ich es noch nie war.", author: "Teilnehmerin eines kostenfreien Workshops" },
  { quote: "Letztes Jahr hatte ich ein Seminar bei Dir, das wirklich mein Leben verändert hat. Mit neuer Kraft und Mut habe ich einen noch verantwortungsvolleren und spannenderen Job angenommen. Ich bin sehr erfolgreich - und mache genau das, was ich immer machen wollte.", author: "Teilnehmerin eines Seminars" },
  { quote: "Luisas Programm und Luisa selbst haben meine Erwartungen mehr als übertroffen. Ich habe so viel über mich gelernt, was mein Selbstvertrauen und Selbstbewusstsein gestärkt hat, wie ich es nicht für möglich gehalten habe. Danke Luisa!", author: "Teilnehmerin eines Coachingprogramms" },
  { quote: "LEUTE, ICH HAB DEN JOB!!! Inklusive meiner Gehaltsvorstellung! Die innere Klarheit hat mir geholfen, die Gelegenheit überhaupt zu erkennen.", author: "Teilnehmerin eines Coachingsprogramms" },
  { quote: "Ich habe direkt am nächsten Tag eine Einladung zum Vorstellungsgespräch erhalten! Danke fürs Beweise schaffen, dass sich die Mühe lohnt - und ich trotz Teilzeit und 3-fach-Mama relevant für den Arbeitsmarkt bin. Oder gerade deshalb.", author: "Teilnehmerin eines Coachingsprogramms" },
];

const imagineJob = [
  "Du hast einen Job, der wirklich Dir entspricht und in dem Du Dein Wesen zum Ausdruck bringen kannst.",
  "Bei dem Du Tätigkeiten machst, die Du auch ohne Geld machen würdest.",
  "In dem Du echten Impact hast und viel MehrWert für andere schaffst.",
  "Der sich sinnstiftend und erfüllend anfühlt.",
  "UND bei all dem richtig viel Geld verdienst.",
];

const aboutParas = [
  { text: "Ich glaube nicht, dass wir uns zwischen Erfolg und Erfüllung entscheiden müssen.", variant: "heading" },
  { text: "Ich habe selbst lange nach den alten Regeln des Arbeitsmarktes gespielt - war Meisterin darin, sie brav zu befolgen, sogar als Selbstständige noch. Bis ich anfing, mich zu fragen:" },
  { text: "Wer hat eigentlich gesagt, dass Arbeit so aussehen muss?", variant: "highlight" },
  { text: "Mein Job war nie verantwortlich dafür, dass es mir schlecht ging. Mein Arbeitgeber auch nicht. Was Erschöpfung erschafft, hat wenig mit der Arbeit an sich zu tun, sondern damit, wie ich mich führe." },
  { text: "Seit 2018 sauge ich alles auf, was mir zum Thema in die Finger kommt - von New Work über Identitätsarbeit bis Nervensystemregulation. Daraus ist mein eigener Traumjob entstanden: ein Leben, in dem sich meine Arbeit auf natürliche Weise einfügt. In dem ich keinen Urlaub von meiner Arbeit „brauche“. Und gleichzeitig so viel reise und das Leben genieße wie nie zuvor." },
  { text: "Die Essenz dessen, was wirklich hilft, teile ich mit Dir in dieser Masterclass - damit Du nicht all die Fehler und Umwege machen brauchst, die ich unterwegs gemacht hab." },
];

/* ---- Primitives ---- */
const ease = [0.22, 1, 0.36, 1];
const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, ease, delay }}>
    {children}
  </motion.div>
);

const Eyebrow = ({ children, dark = false }) => (
  <span className={`inline-flex items-center gap-3 text-[11px] sm:text-xs font-body font-semibold tracking-[0.34em] uppercase ${dark ? "text-[#EBC479]" : "text-[#8A4A2E]"}`}>
    <span className={`h-px w-8 ${dark ? "bg-[#EBC479]" : "bg-[#B26B3F]"} opacity-80`} />
    {children}
  </span>
);

const CTAButton = ({ onClick, children, testid, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`group relative inline-flex items-center justify-center gap-3 rounded-sm bg-[#5C1D10] px-9 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#FFF2B0] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-16px_rgba(92,29,16,0.7)] sm:text-base ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

/* ---- Signup Modal ---- */
const SignupModal = ({ open, onClose, source = "eigentlich-masterclass", successPath = "/eigentlich/fast-geschafft" }) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState("idle"); // idle | sending | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset the form whenever the modal opens.
  useEffect(() => {
    if (open) {
      setStatus("idle");
      setMessage("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setMessage("");
    try {
      const res = await fetch(`${API}/api/masterclass-signups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: firstName, email, consent: true, company, source, ...getStoredUtms() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Anmeldung fehlgeschlagen.");
      onClose();
      navigate(successPath);
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Bitte versuche es erneut.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="signup-modal"
        >
          <motion.div className="absolute inset-0 bg-[#2A0F08]/80 backdrop-blur-sm" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.div
            className="relative w-full max-w-md overflow-hidden rounded-lg border border-[#EBC479]/50 bg-gradient-to-b from-[#FFFBEE] to-[#F6E7BE] p-7 shadow-2xl sm:p-9"
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease }}
          >
            <button onClick={onClose} aria-label="Schließen" data-testid="signup-modal-close" className="absolute right-4 top-4 text-[#8A4A2E] transition-colors hover:text-[#5C1D10]">
              <X className="h-5 w-5" />
            </button>

            {(
              <>
                <h3 className="font-heading text-2xl font-bold leading-tight text-[#5C1D10] sm:text-3xl">Melde Dich hier kostenlos an</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#5C1D10]/80">
                  Trage Deinen Namen und Deine E-Mail-Adresse ein, um Dich für die Masterclass <span className="font-semibold">Bye, Bye „Eigentlich ist doch alles gut“</span> anzumelden.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="absolute left-[-9999px]" aria-hidden="true">
                    <label htmlFor="mc-company">Firma (bitte leer lassen)</label>
                    <input id="mc-company" type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="mc-name" className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-wider text-[#8A4A2E]">Name</label>
                    <input id="mc-name" data-testid="signup-name-input" type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Dein Vorname"
                      className="w-full rounded-sm border border-[#C99A5B] bg-white/70 px-4 py-3 text-[#3E1508] outline-none transition-colors placeholder:text-[#8A4A2E]/50 focus:border-[#5C1D10]" />
                  </div>
                  <div>
                    <label htmlFor="mc-email" className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-wider text-[#8A4A2E]">E-Mail-Adresse</label>
                    <input id="mc-email" data-testid="signup-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="deine@email.de"
                      className="w-full rounded-sm border border-[#C99A5B] bg-white/70 px-4 py-3 text-[#3E1508] outline-none transition-colors placeholder:text-[#8A4A2E]/50 focus:border-[#5C1D10]" />
                  </div>

                  <p className="text-[11px] leading-relaxed text-[#5C1D10]/70">
                    Mit Anmeldung für die Masterclass erklärst Du Dich damit einverstanden, E-Mails von mir rund um die Masterclass und meinen Newsletter zu erhalten. Du kannst Deine Zustimmung jederzeit widerrufen.{" "}
                    <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[#5C1D10]">Datenschutzerklärung</a>
                  </p>

                  {status === "error" && <p data-testid="signup-error" className="text-sm font-medium text-[#B00020]">{message}</p>}

                  <button type="submit" disabled={status === "sending"} data-testid="signup-submit-button"
                    className="group flex w-full items-center justify-center gap-2 rounded-sm bg-[#5C1D10] px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#FFF2B0] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_44px_-16px_rgba(92,29,16,0.7)] disabled:cursor-not-allowed disabled:opacity-60">
                    {status === "sending" ? (<><Loader2 className="h-4 w-4 animate-spin" /> Wird gesendet …</>) : "Jetzt kostenlos anmelden"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---- Hero ---- */
const Hero = ({ onOpen, showDateTime = true }) => (
  <section className="relative overflow-hidden bg-gradient-to-b from-[#FBEFC9] to-[#F4DFA6] pt-10 pb-16 lg:pt-14 lg:pb-20" data-testid="eigentlich-hero">
    <div className="mx-auto max-w-6xl px-5 lg:px-10">
      <Reveal>
        <p className="mb-5 text-center font-heading text-xs font-semibold uppercase tracking-[0.34em] text-[#8A4A2E] sm:text-sm" data-testid="hero-label">
          LIMONATA Coaching präsentiert:
        </p>
        <div className="overflow-hidden rounded-xl border border-[#EBC479]/60 shadow-[0_30px_80px_-30px_rgba(92,29,16,0.45)]">
          <img src={HERO_HOCH} alt="Kostenfreie Masterclass - Bye, Bye „Eigentlich ist doch alles gut“ mit Luisa Bergholz" className="block w-full sm:hidden" />
          <img src={HERO_QUER} alt="Kostenfreie Masterclass - Bye, Bye „Eigentlich ist doch alles gut“ mit Luisa Bergholz" className="hidden w-full sm:block" />
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-10 flex flex-col items-center text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-heading text-sm font-semibold uppercase tracking-[0.18em] text-[#5C1D10] sm:text-base">
            {showDateTime && (
              <>
                <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#B26B3F]" /> 30. September 2026</span>
                <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#B26B3F]" /> 19:00 Uhr</span>
              </>
            )}
            <span className="inline-flex items-center gap-2"><Video className="h-4 w-4 text-[#B26B3F]" /> Live via Zoom</span>
          </div>
          <CTAButton onClick={onOpen} testid="hero-cta" className="mt-7">Melde Dich hier kostenlos an</CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---- Story (worum es geht) ---- */
const Story = () => {
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.muted = true;
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-[#F4DFA6] py-20 lg:py-24" data-testid="intro-topic">
      {/* Narrative */}
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <h2 className="font-heading text-4xl font-bold leading-[1.05] text-[#5C1D10] sm:text-5xl">
            Du hast <span className="italic">eigentlich</span> alles.
          </h2>
        </Reveal>

        <div className="mt-8 space-y-5 text-base leading-relaxed text-[#5C1D10]/85 sm:text-lg">
          <Reveal delay={0.05}>
            <p>Den Job, der gut aussieht und um den andere Dich beneiden. Viel Verantwortung und ein gutes Ansehen im Unternehmen. Jeden Monat ein gutes Gehalt auf Deinem Konto, das Dir einen Lebensstandard ermöglicht, von dem Du mal geträumt hast.</p>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-heading text-xl font-semibold text-[#5C1D10] sm:text-2xl">Und trotzdem ist da diese Sehnsucht nach mehr.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>Die lauter wird, wenn's besonders stressig im Projekt ist, Dein Kollege seinen Frust an Dir rauslässt, der Kunde nicht mal Danke sagt für die Extra-Arbeit, die Du gemacht hast.</p>
          </Reveal>
          <Reveal delay={0.12}>
            <p>Oder wenn Du das Treffen mit der Freundin, auf das Du Dich <span className="italic">eigentlich</span> so gefreut hattest, absagst, weil Du zu erschöpft bist und außer Couch und Lieferdienst gerade gar nix mehr geht.</p>
          </Reveal>
          <Reveal delay={0.14}>
            <p>Aber sobald Du darüber nachdenkst, wirklich etwas zu verändern, sagst Du Dir: <span className="italic">„Ach, eigentlich ist doch alles gut. Was will ich denn noch?“</span></p>
          </Reveal>
        </div>

        <Reveal delay={0.16}>
          <div className="mt-10">
            <p className="font-accent text-lg italic text-[#8A4A2E]">Allerdings:</p>
            <p className="mt-1 font-heading text-3xl font-black text-[#5C1D10] sm:text-4xl">Es ist eben nicht gut.</p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-[#5C1D10]/85 sm:text-lg">Diese Sehnsucht ist nicht ohne Grund da. Und sie geht auch nicht weg, nur weil Du sie ignorierst.</p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-6 text-base leading-relaxed text-[#5C1D10]/85 sm:text-lg">
            In dieser Masterclass lade ich Dich ein, hinter das „<span className="italic">eigentlich</span>“ zu schauen. Dich selbst und Deine Sehnsucht ernst zu nehmen. Und loszugehen für richtig gut - ohne Abstriche.
          </p>
          <p className="mt-4 font-heading text-lg font-semibold leading-relaxed text-[#5C1D10] sm:text-xl">
            Es ist kein Erfolg, wenn Du dafür andere Bereiche Deines Lebens opfern musst.
          </p>
        </Reveal>

        {/* Vision */}
        <Reveal delay={0.24}>
          <div className="relative mt-12 overflow-hidden rounded-2xl bg-gradient-to-br from-[#5C1D10] via-[#6B2A12] to-[#A9793A] p-8 shadow-[0_30px_80px_-30px_rgba(92,29,16,0.6)] sm:p-12">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#FFF2B0] opacity-25 blur-3xl" />
            <p className="relative font-heading text-2xl font-semibold text-[#FFF8D2] sm:text-3xl">Stell Dir mal vor …</p>
            <ul className="relative mt-7 space-y-5">
              {imagineJob.map((t, i) => (
                <li key={i} className="flex gap-3 text-base leading-relaxed text-[#FFF6E2]/95 sm:text-lg">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FFF2B0]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.26}>
          <p className="mx-auto mt-10 max-w-2xl text-center font-accent text-2xl italic leading-relaxed text-[#8A4A2E] sm:text-3xl">
            Nur weil Du es bisher nicht für möglich hältst, heißt es nicht, dass es nicht für Dich möglich ist.
          </p>
          <p className="mt-6 text-center font-heading text-base font-bold uppercase tracking-[0.14em] text-[#B26B3F]">
            In dieser Masterclass zeige ich Dir, wie.
          </p>
        </Reveal>
      </div>

      {/* Bye, Bye + Video */}
      <div className="mt-16 bg-[#3A130A] py-16 lg:mt-20 lg:py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 lg:grid-cols-[1fr_0.9fr] lg:gap-12 lg:px-10">
          <Reveal>
            <h2 className="font-heading text-4xl font-black leading-[1.05] text-[#FFF2B0] sm:text-5xl lg:text-right lg:text-6xl">
              Bye, Bye <br />„<span className="italic">Eigentlich</span> ist doch alles gut“.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mx-auto max-w-sm overflow-hidden rounded-xl border border-[#EBC479]/60 shadow-[0_24px_70px_-28px_rgba(0,0,0,0.6)] lg:mx-0">
              <video ref={videoRef} autoPlay muted playsInline loop preload="auto" disablePictureInPicture className="pointer-events-none block h-full w-full bg-[#2A0F08]" data-testid="intro-video-player">
                <source src={INTRO_VIDEO_WEBM} type="video/webm" />
                <source src={INTRO_VIDEO_MP4} type="video/mp4" />
              </video>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mx-auto mt-16 max-w-3xl px-6 text-center">
          <p className="font-heading text-3xl font-bold leading-snug text-[#5C1D10] sm:text-4xl">
            Du verbringst mehr Zeit mit Deinem Job als mit Deinen Liebsten.
          </p>
          <p className="mt-4 font-accent text-xl italic text-[#8A4A2E] sm:text-2xl">
            Wie wär's, wenn sich diese Zeit auch genauso gut anfühlt?
          </p>
        </div>
      </Reveal>
    </section>
  );
};

/* ---- Testimonials ---- */
const Testimonials = () => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);
  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  return (
    <section className="relative bg-[#FBEFC9] py-24 lg:py-32" data-testid="eigentlich-testimonials">
      <div className="mx-auto max-w-4xl px-6">
        <Reveal><div className="flex justify-center"><Eyebrow>Kundinnenerfolge</Eyebrow></div></Reveal>
        <div className="mt-12 overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {testimonials.map((t, i) => (
              <div key={i} className="min-w-0 flex-[0_0_100%] px-4">
                <div className="flex h-full flex-col items-center text-center">
                  <blockquote className="font-accent text-lg italic leading-relaxed text-[#5C1D10] sm:text-2xl">„{t.quote}“</blockquote>
                  <p className="mt-6 font-heading text-xs font-semibold uppercase tracking-widest text-[#8A4A2E]">{t.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-4">
          <button onClick={() => embla && embla.scrollPrev()} aria-label="Vorheriges" data-testid="testimonial-prev" className="rounded-full border border-[#B26B3F]/40 p-2 text-[#B26B3F] transition-colors hover:bg-[#B26B3F]/10">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => scrollTo(i)} data-testid={`testimonial-dot-${i}`} aria-label={`Stimme ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${selected === i ? "w-6 bg-[#B26B3F]" : "w-2 bg-[#B26B3F]/35"}`} />
            ))}
          </div>
          <button onClick={() => embla && embla.scrollNext()} aria-label="Nächstes" data-testid="testimonial-next" className="rounded-full border border-[#B26B3F]/40 p-2 text-[#B26B3F] transition-colors hover:bg-[#B26B3F]/10">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ---- About ---- */
const About = () => (
  <section className="relative bg-gradient-to-b from-[#FBEFC9] to-[#F4DFA6] py-24 lg:py-32" data-testid="eigentlich-about">
    <div className="mx-auto grid max-w-6xl items-start gap-12 px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:px-10">
      <Reveal className="lg:sticky lg:top-16">
        <div className="overflow-hidden rounded-xl border border-[#EBC479]/60 shadow-[0_24px_70px_-28px_rgba(92,29,16,0.45)]">
          <img src={ABOUT_PORTRAIT} alt="Luisa Bergholz" className="block w-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Eyebrow>Deine Gastgeberin</Eyebrow>
        <h2 className="mt-6 font-heading text-4xl font-bold tracking-tight text-[#5C1D10] sm:text-5xl">Luisa Bergholz</h2>
        <p className="mt-4 font-accent text-lg italic leading-relaxed text-[#8A4A2E] sm:text-xl">
          Arbeitspsychologin · Expertin für berufliche Neuorientierung · Projektorin · Dolce-Vita-Genießerin
        </p>
        <div className="mt-8 space-y-5">
          {aboutParas.map((p, i) => {
            if (p.variant === "heading")
              return <p key={i} className="font-heading text-xl font-semibold leading-relaxed text-[#5C1D10] sm:text-2xl">{p.text}</p>;
            if (p.variant === "highlight")
              return <p key={i} className="border-l-4 border-[#B26B3F] pl-5 font-accent text-2xl font-semibold italic leading-snug text-[#5C1D10] sm:text-3xl">{p.text}</p>;
            return <p key={i} className="text-base leading-relaxed text-[#5C1D10]/85 sm:text-lg">{p.text}</p>;
          })}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---- Final CTA ---- */
const FinalCTA = ({ onOpen, showDateTime = true }) => (
  <section className="relative overflow-hidden bg-[#5C1D10] py-24 text-center lg:py-32" data-testid="eigentlich-final-cta">
    <div className="pointer-events-none absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_center,rgba(235,196,121,0.35),transparent_60%)]" />
    <div className="relative mx-auto max-w-2xl px-6">
      <Reveal>
        <h2 className="font-heading text-4xl font-black leading-[1.05] text-[#FFF2B0] sm:text-5xl">Sei live dabei.</h2>
        <p className="mt-6 font-heading text-lg font-semibold uppercase tracking-[0.18em] text-[#EBC479]">
          {showDateTime ? "30. September 2026 · 19:00 Uhr · Kostenfrei" : "Live via Zoom · Kostenfrei"}
        </p>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#FBEFC9]/85 sm:text-lg">
          Für einen Job, der sich genauso gut anfühlt, wie er von außen aussieht.
        </p>
        <div className="mt-9 flex justify-center">
          <button type="button" onClick={onOpen} data-testid="final-cta"
            className="group inline-flex items-center justify-center gap-3 rounded-sm bg-[#FFF2B0] px-10 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#5C1D10] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-16px_rgba(255,242,176,0.5)] sm:text-base">
            Melde Dich hier kostenlos an
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
        <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-[#FBEFC9]/70">
          Und falls es live nicht klappt: Ja, es wird eine Aufzeichnung geben.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---- Footer ---- */
const Footer = () => (
  <footer className="bg-[#2A0F08] py-14" data-testid="eigentlich-footer">
    <div className="mx-auto max-w-5xl px-6">
      <div className="flex flex-col items-center gap-6">
        <p className="font-heading text-sm uppercase tracking-[0.3em] text-[#EBC479]">by Luisa Bergholz</p>
        <div className="flex items-center gap-5">
          <a href="https://www.instagram.com/luisa.bergholz/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="text-[#C9A96A] transition-colors hover:text-[#FFF2B0]"><Instagram className="h-5 w-5" /></a>
          <a href="https://www.youtube.com/@luisa.bergholz" target="_blank" rel="noopener noreferrer" data-testid="footer-youtube" aria-label="YouTube" className="text-[#C9A96A] transition-colors hover:text-[#FFF2B0]"><Youtube className="h-5 w-5" /></a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#C9A96A]">
          <a href="https://luisabergholz.com/impressum" target="_blank" rel="noopener noreferrer" data-testid="footer-impressum" className="transition-colors hover:text-[#FFF2B0]">Impressum</a>
          <a href="https://luisabergholz.com/datenschutzerklaerung" target="_blank" rel="noopener noreferrer" data-testid="footer-datenschutz" className="transition-colors hover:text-[#FFF2B0]">Datenschutz</a>
          <a href="https://luisabergholz.com/agb" target="_blank" rel="noopener noreferrer" data-testid="footer-agb" className="transition-colors hover:text-[#FFF2B0]">AGB</a>
          <a href="https://luisabergholz.com/widerrufsformular" target="_blank" rel="noopener noreferrer" data-testid="footer-widerruf" className="transition-colors hover:text-[#FFF2B0]">Vertrag widerrufen</a>
          <a href="https://luisabergholz.com/kontakt" target="_blank" rel="noopener noreferrer" data-testid="footer-kontakt" className="transition-colors hover:text-[#FFF2B0]">Kontakt</a>
        </div>
      </div>
    </div>
  </footer>
);

/* ---- Page ---- */
export default function EigentlichPage({ source = "eigentlich-masterclass", showDateTime = true, successPath = "/eigentlich/fast-geschafft" }) {
  const [modalOpen, setModalOpen] = useState(false);
  const open = () => setModalOpen(true);

  useEffect(() => {
    captureUtms();
  }, []);

  return (
    <div className="bg-[#F4DFA6]" data-testid="eigentlich-page">
      <Hero onOpen={open} showDateTime={showDateTime} />
      <Story />
      <Testimonials />
      <About />
      <FinalCTA onOpen={open} showDateTime={showDateTime} />
      <Footer />
      <SignupModal open={modalOpen} onClose={() => setModalOpen(false)} source={source} successPath={successPath} />
    </div>
  );
}
